// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * Pure layout math for the Results band (MVP WS2, AC2.3). The band has two
 * modes: an `inline` band pinned to the bottom of the canvas whose height the
 * user can drag-resize, and a `maximized` overlay that floats above the canvas.
 *
 * "Maximize/expand" and "pop-out overlay" from the AC are the same maximized
 * affordance in the MVP -- a single large overlay over the canvas. Multi-panel
 * dashboards and separate OS windows are explicitly post-MVP, so one overlay
 * mode covers both without extra state. The Editor owns the BandLayout; this
 * module is the testable core behind the drag handle and the maximize button.
 */

/** The two Results-band presentations. */
export type ResultsBandMode = 'inline' | 'maximized';

/** Smallest inline height (px) -- below this the chart is unreadable. */
export const MIN_BAND_HEIGHT = 96;
/** Largest inline height (px) before the band should be maximized instead. */
export const MAX_BAND_HEIGHT = 360;
/** Default inline chart height (px). */
export const DEFAULT_BAND_HEIGHT = 120;
/** Chart height (px) used when the band is maximized into the overlay. */
export const MAXIMIZED_BAND_HEIGHT = 480;

export interface BandLayout {
  /** Which presentation the band is in. */
  mode: ResultsBandMode;
  /** The user-chosen inline chart height in px (drag-resizable). */
  inlineHeight: number;
}

export const DEFAULT_BAND_LAYOUT: BandLayout = {
  mode: 'inline',
  inlineHeight: DEFAULT_BAND_HEIGHT,
};

/** Clamp an inline height into the readable [MIN, MAX] range, rounding to px. */
export function clampBandHeight(height: number): number {
  if (!Number.isFinite(height)) {
    return DEFAULT_BAND_HEIGHT;
  }
  return Math.max(MIN_BAND_HEIGHT, Math.min(MAX_BAND_HEIGHT, Math.round(height)));
}

/**
 * The new inline height while dragging the band's top edge. The handle lives on
 * top, so dragging up (currentClientY < startClientY) grows the band and
 * dragging down shrinks it. Result is clamped to the readable range.
 */
export function resizeInlineHeight(startHeight: number, startClientY: number, currentClientY: number): number {
  return clampBandHeight(startHeight + (startClientY - currentClientY));
}

/** Toggle between the inline band and the maximized overlay. */
export function toggleMaximized(layout: BandLayout): BandLayout {
  return { ...layout, mode: layout.mode === 'maximized' ? 'inline' : 'maximized' };
}

/** The chart height to render for the current layout. */
export function chartHeight(layout: BandLayout): number {
  return layout.mode === 'maximized' ? MAXIMIZED_BAND_HEIGHT : clampBandHeight(layout.inlineHeight);
}
